import React, { useState, useEffect, useMemo } from 'react';
import API from '../services/api';
import { API_BASE_URL } from '../constants/config';
import { THEME } from '../constants/theme'; 

// PANELS IMPORT
import BikeHistoryPanel from '../components/Panels/BikeHistoryPanel';
import DashboardHome from '../components/Panels/DashboardHome';
import SalesmanPanel from '../components/Panels/SalesmanPanel';
import AccountantPanel from '../components/Panels/AccountantPanel';
import PurchasePanel from '../components/Panels/PurchasePanel';
import DocPanel from '../components/Panels/DocPanel'; 
import HRPanel from '../components/Panels/HRPanel';
import CustomerCarePanel from '../components/Panels/CustomerCarePanel';
import ComplaintsModule from '../components/Panels/ComplaintsModule';
import OthersPanel from '../components/Panels/OthersPanel';
import ETOPanel from '../components/Panels/ETOPanel';

const EmployeeDashboard = () => {
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });

    const [activeTab, setActiveTab] = useState('dashboard');
    const [pendingSubTab, setPendingSubTab] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true); // 👈 Sidebar Open/Close State

    useEffect(() => {
        if (!user) {
            window.location.href = '/login';
        }
    }, [user]);

    const rolesArray = useMemo(() => {
        return user?.roles ? user.roles.split(',').filter(r => r.trim() !== "") : [];
    }, [user?.roles]);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/login';
    };

    const handleNavigate = (role, subTab) => {
        if (!role || !rolesArray.includes(role)) return; 
        setPendingSubTab(subTab || null);
        setActiveTab(role);
    };

    const getIcon = (role) => {
        const icons = {
            'dashboard': '📊',
            'bike_history': '🔄',
            'Salesman': '🛍️',
            'Accountant': '💳',
            'Purchase_Manager': '📦',
            'Doc_Department': '📂',
            'ETO_Manager': '📋',
            'ETO': '📋',
            'HR_Manager': '👥',
            'Customer_Care': '🎧',
            'Others': '📑'
        };
        return icons[role] || '⭐';
    };

    const formatRoleName = (role) => {
        if (role === 'dashboard') return 'Dashboard';
        if (role === 'ETO_Manager' || role === 'ETO') return 'ETO Panel';
        if (role === 'bike_history') return 'Bike History';
        return role.replace('_', ' ');
    };

    const renderContent = () => {
        if (activeTab === 'dashboard') {
            return (
                <DashboardHome
                    rolesArray={rolesArray}
                    userName={user?.full_name}
                    onNavigate={handleNavigate}
                />
            );
        }

        if (rolesArray.length === 0) return (
            <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100%', color: '#94a3b8'}}>
                <h3>No Panels Assigned. Contact Admin.</h3>
            </div>
        );

        switch (activeTab) {
            case 'bike_history': return <BikeHistoryPanel />;
            case 'Salesman': return <SalesmanPanel />;
            case 'Accountant': return <AccountantPanel initialTab={pendingSubTab} />;
            case 'Purchase_Manager': return <PurchasePanel initialSubTab={pendingSubTab} />;
            case 'Doc_Department': return <DocPanel />; 
            case 'ETO': return <ETOPanel />;
            case 'HR_Manager': return <HRPanel />;
            case 'Customer_Care': return <ComplaintsModule initialView={pendingSubTab} />;
            case 'Others': return <OthersPanel />;
            default: return <div style={{padding: '50px', textAlign: 'center'}}>Select a panel.</div>;
        }
    };

    if (!user) return null;

    return (
        <div style={styles.container}>
            {/* --- ULTRA PROFESSIONAL COLLAPSIBLE SIDEBAR --- */}
            <aside style={{ ...styles.sidebar, width: isSidebarOpen ? '280px' : '80px' }}>
               <div style={styles.logoContainer}>
                    <div style={styles.logoIcon}>UM</div>
                    {isSidebarOpen && (
                        <div style={styles.logoText}>
                            <span style={{ fontWeight: '800', fontSize: '15px', color: '#fff', letterSpacing: '0.8px' }}>
                                {THEME.footerCompany.toUpperCase()}
                            </span>
                            <span style={{ display: 'block', fontSize: '9.5px', color: '#64748b', fontWeight: '500', letterSpacing: '1.2px' }}>
                                MANAGEMENT PORTAL
                            </span>
                        </div>
                    )}
                </div>

                {isSidebarOpen && <div style={styles.navHeader}>MAIN MENU</div>}
                
                <nav style={{ flex: 1, padding: isSidebarOpen ? '0 14px' : '0 10px', overflowY: 'auto', overflowX: 'hidden' }}>
                    
                    {/* Dashboard Tab */}
                    <div
                        onClick={() => setActiveTab('dashboard')}
                        style={activeTab === 'dashboard' ? styles.activeNav : styles.navItem}
                        title={!isSidebarOpen ? "Dashboard" : ""}
                    >
                        <span style={styles.iconSpan}>{getIcon('dashboard')}</span>
                        {isSidebarOpen && <span style={styles.navText}>Dashboard</span>}
                        {isSidebarOpen && activeTab === 'dashboard' && <div style={styles.activeIndicator} />}
                    </div>

                    {/* Bike History Tab */}
                    <div
                        onClick={() => { setPendingSubTab(null); setActiveTab('bike_history'); }}
                        style={activeTab === 'bike_history' ? styles.activeNav : styles.navItem}
                        title={!isSidebarOpen ? "Bike History" : ""}
                    >
                        <span style={styles.iconSpan}>{getIcon('bike_history')}</span>
                        {isSidebarOpen && <span style={styles.navText}>Bike History</span>}
                        {isSidebarOpen && activeTab === 'bike_history' && <div style={styles.activeIndicator} />}
                    </div>

                    {/* Roles Mapping */}
                    {rolesArray.map((role) => (
                        <div
                            key={role}
                            onClick={() => { setPendingSubTab(null); setActiveTab(role); }}
                            style={activeTab === role ? styles.activeNav : styles.navItem}
                            title={!isSidebarOpen ? formatRoleName(role) : ""}
                        >
                            <span style={styles.iconSpan}>{getIcon(role)}</span>
                            {isSidebarOpen && <span style={styles.navText}>{formatRoleName(role)}</span>}
                            {isSidebarOpen && activeTab === role && <div style={styles.activeIndicator} />}
                        </div>
                    ))}
                </nav>

                <div style={styles.sidebarFooter}>
                    <button onClick={handleLogout} style={styles.logoutBtn} title={!isSidebarOpen ? "Logout" : ""}>
                        <span style={{ fontSize: '15px' }}>🚪</span> {isSidebarOpen && <span style={{marginLeft: '8px'}}>Logout System</span>}
                    </button>
                </div>
            </aside>

            {/* --- MAIN CONTENT AREA --- */}
            <div style={styles.mainArea}>
                <header style={styles.header}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                        {/* 🍔 Toggle Menu Button */}
                        <button 
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                            style={styles.menuToggleBtn}
                            title="Toggle Sidebar"
                        >
                            ☰
                        </button>
                        <div>
                            <h2 style={styles.tabTitle}>
                                {activeTab === 'dashboard' ? 'Dashboard Overview' : `${formatRoleName(activeTab)} Panel`}
                            </h2>
                            <div style={styles.breadcrumb}>
                                {activeTab === 'dashboard' ? 'Dashboard / Overview' : `Dashboard / ${formatRoleName(activeTab)}`}
                            </div>
                        </div>
                    </div>

                    <div style={styles.profile}>
                        <div style={{textAlign: 'right', marginRight: '14px'}}>
                            <div style={styles.userName}>{user.full_name}</div>
                            <div style={styles.userStatus}>● Online Session</div>
                        </div>
                        <div style={styles.avatar}>{user.full_name?.charAt(0)}</div>
                    </div>
                </header>

                <main style={styles.content}>
                    {renderContent()}
                </main>
                
                <footer style={styles.footer}>
                    <div>© {THEME.year} {THEME.footerCompany} | Enterprise Portal v{THEME.footerVersion || '1.0'}</div>
                </footer>
            </div>
        </div>
    );
};

const styles = {
    container: { display: 'flex', height: '100vh', backgroundColor: '#f8fafc', fontFamily: "'Plus Jakarta Sans', 'Inter', 'Segoe UI', sans-serif" },
    
    // Sidebar Design Upgrades with Smooth Transition
    sidebar: { 
        backgroundColor: '#0b1329', 
        display: 'flex', 
        flexDirection: 'column', 
        boxShadow: '4px 0 25px rgba(11, 19, 41, 0.08)', 
        zIndex: 10,
        borderRight: '1px solid rgba(255, 255, 255, 0.04)',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflowX: 'hidden'
    },
    logoContainer: { 
        padding: '22px 18px', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '14px', 
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        backgroundColor: 'rgba(255, 255, 255, 0.01)',
        whiteSpace: 'nowrap'
    },
    logoIcon: { 
        background: `linear-gradient(135deg, ${THEME.secondary}, #1d4ed8)`, 
        color: 'white', 
        minWidth: '42px', 
        width: '42px', 
        height: '42px', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        borderRadius: '12px', 
        fontWeight: '800', 
        fontSize: '15px',
        boxShadow: `0 8px 20px ${THEME.secondary}44`,
        letterSpacing: '0.5px'
    },
    logoText: {
        overflow: 'hidden'
    },
    navHeader: { 
        padding: '24px 18px 8px', 
        fontSize: '10px', 
        fontWeight: '700', 
        color: '#475569', 
        letterSpacing: '1.8px',
        whiteSpace: 'nowrap'
    },
    navItem: { 
        display: 'flex', 
        alignItems: 'center', 
        padding: '11px 14px', 
        color: '#94a3b8', 
        cursor: 'pointer', 
        borderRadius: '10px', 
        marginBottom: '5px', 
        transition: 'all 0.2s ease', 
        fontSize: '13.5px',
        fontWeight: '500',
        userSelect: 'none',
        whiteSpace: 'nowrap'
    },
    activeNav: { 
        display: 'flex', 
        alignItems: 'center', 
        padding: '11px 14px', 
        color: '#ffffff', 
        backgroundColor: THEME.secondary, 
        borderRadius: '10px', 
        marginBottom: '5px', 
        fontWeight: '600', 
        boxShadow: `0 6px 20px ${THEME.secondary}40`, 
        fontSize: '13.5px',
        position: 'relative',
        userSelect: 'none',
        whiteSpace: 'nowrap'
    },
    iconSpan: { 
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '24px',
        marginRight: '12px'
    },
    navText: {
        flex: 1,
        letterSpacing: '0.2px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    activeIndicator: { 
        width: '5px', 
        height: '18px', 
        borderRadius: '4px', 
        backgroundColor: '#ffffff',
        boxShadow: '0 0 8px rgba(255,255,255,0.6)'
    },
    sidebarFooter: { 
        padding: '16px 14px', 
        borderTop: '1px solid rgba(255,255,255,0.06)',
        backgroundColor: 'rgba(0,0,0,0.1)'
    },
    logoutBtn: { 
        width: '100%', 
        padding: '11px', 
        background: 'rgba(239, 68, 68, 0.08)', 
        color: '#f87171', 
        border: '1px solid rgba(239, 68, 68, 0.15)', 
        borderRadius: '10px', 
        cursor: 'pointer', 
        fontWeight: '600', 
        fontSize: '13px',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap'
    },
    
    // Main Area Styling
    mainArea: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header: { height: '75px', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 30px', borderBottom: '1px solid #f1f5f9' },
    menuToggleBtn: {
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        color: '#334155',
        fontSize: '18px',
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease'
    },
    tabTitle: { margin: 0, fontSize: '19px', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.3px' },
    breadcrumb: { fontSize: '11.5px', color: '#64748b', marginTop: '2px', fontWeight: '500' },
    profile: { display: 'flex', alignItems: 'center' },
    userName: { fontWeight: '700', color: '#1e293b', fontSize: '14px' },
    userStatus: { fontSize: '10.5px', color: '#10b981', fontWeight: '700', letterSpacing: '0.3px' },
    avatar: { width: '40px', height: '40px', background: '#f8fafc', color: THEME.secondary, borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: '700', fontSize: '15px', border: '1px solid #e2e8f0', boxShadow: '0 2px 5px rgba(0,0,0,0.02)' },
    content: { flex: 1, padding: '28px 30px', overflowY: 'auto', backgroundColor: '#f8fafc' },
    footer: { height: '48px', padding: '0 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11.5px', color: '#64748b', background: '#ffffff', borderTop: '1px solid #f1f5f9', fontWeight: '500' }
};

export default EmployeeDashboard;